import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { motion, useInView, useScroll, useTransform, useSpring, useMotionValue, AnimatePresence } from "framer-motion";
import {
  Brain,
  MessageSquare,
  Map,
  Target,
  Users,
  AlertTriangle,
  Building2,
  TrendingDown,
  GraduationCap,
  Briefcase,
  RefreshCw,
  Cpu,
  Link,
  Check,
  X,
  ArrowRight,
  ArrowUpRight,
  ArrowLeft,
  Mail,
  Linkedin,
  Globe,
  CheckCircle2,
  Calendar,
  Menu,
  X as XIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Newspaper
} from "lucide-react";

// CASEY Logo URLs - SVG logos
const CASEY_LOGOS = {
  dark: "https://customer-assets.emergentagent.com/job_investor-casey/artifacts/fcxzuz8r_5.svg", // For light/white backgrounds (dark logo)
  light: "https://customer-assets.emergentagent.com/job_investor-casey/artifacts/k0cvyyz4_6.svg", // For dark backgrounds (light/white logo)
};

// Dark Mode Context
const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

export const DarkModeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false); // Default to light mode

  useEffect(() => {
    // Only read from localStorage after initial render
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      setIsDark(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDark));
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleDarkMode = () => setIsDark(!isDark);

  return (
    <DarkModeContext.Provider value={{ isDark, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

// Dark Mode Toggle Component
// Dark Mode Toggle Component - Muted style for static header
const DarkModeToggle = () => {
  const { isDark, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="relative w-9 h-9 rounded-full flex items-center justify-center bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition-colors"
      aria-label="Toggle dark mode"
    >
      <AnimatePresence mode="wait">
        {isDark ? (
          <motion.div
            key="sun"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Sun className="w-4 h-4" />
          </motion.div>
        ) : (
          <motion.div
            key="moon"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Moon className="w-4 h-4" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};
import {
  companyInfo,
  problemStats,
  problemDetails,
  solutionModules,
  marketData,
  competitiveData,
  moatData,
  businessModel,
  roadmapData,
  teamMembers,
  investmentAsk
} from "../data/mock";
import "./InvestorDeckV3.css";

// Custom Cursor Component - Universal visibility on all backgrounds
const CustomCursor = () => {
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  useEffect(() => {
    const moveCursor = (e) => {
      cursorX.set(e.clientX - 16);
      cursorY.set(e.clientY - 16);
    };
    window.addEventListener("mousemove", moveCursor);
    return () => window.removeEventListener("mousemove", moveCursor);
  }, [cursorX, cursorY]);

  return (
    <motion.div
      className="fixed w-8 h-8 pointer-events-none z-[9999] hidden md:block"
      style={{ x: cursorX, y: cursorY }}
    >
      {/* Dual-ring cursor for visibility on any background */}
      <div 
        className="w-full h-full rounded-full"
        style={{
          border: '2px solid white',
          boxShadow: '0 0 0 2px #011bc4, 0 0 15px rgba(1, 27, 196, 0.6), inset 0 0 8px rgba(255, 255, 255, 0.3)',
          backgroundColor: 'rgba(1, 27, 196, 0.4)',
        }}
      />
    </motion.div>
  );
};

// Text Scramble Effect
const ScrambleText = ({ text, className = "" }) => {
  const [displayText, setDisplayText] = useState(text);
  const [isHovered, setIsHovered] = useState(false);
  const chars = "!<>-_\\/[]{}—=+*^?#_";

  useEffect(() => {
    if (!isHovered) {
      setDisplayText(text);
      return;
    }

    let iteration = 0;
    const interval = setInterval(() => {
      setDisplayText(
        text
          .split("")
          .map((letter, index) => {
            if (index < iteration) return text[index];
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join("")
      );
      if (iteration >= text.length) clearInterval(interval);
      iteration += 1 / 3;
    }, 30);

    return () => clearInterval(interval);
  }, [isHovered, text]);

  return (
    <span
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {displayText}
    </span>
  );
};

// Animated Number with morphing effect
const MorphNumber = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericValue = parseInt(value.toString().replace(/[^0-9]/g, ""));

  useEffect(() => {
    if (isInView && numericValue) {
      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, numericValue]);

  return (
    <span ref={ref} className="tabular-nums font-montserrat">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Reveal Text Animation
const RevealText = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });

  return (
    <div ref={ref} className={`overflow-hidden pb-2 ${className}`}>
      <motion.div
        initial={{ y: "100%" }}
        animate={isInView ? { y: 0 } : {}}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Horizontal Scroll Section
const HorizontalScroll = ({ children }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-75%"]);

  return (
    <section ref={containerRef} className="relative h-[300vh]">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <motion.div style={{ x }} className="flex gap-8 pl-[10vw]">
          {children}
        </motion.div>
      </div>
    </section>
  );
};

// Navigation - Static with subtle dark overlay
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDark } = useDarkMode();

  const navItems = [
    { label: "Problem", href: "#problem" },
    { label: "Solution", href: "#solution" },
    { label: "Market", href: "#market" },
    { label: "Team", href: "#team" },
    { label: "Investment", href: "#investment" }
  ];

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-slate-900/70 backdrop-blur-md border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <motion.a
          href="#"
          className="flex items-center group"
          whileHover={{ scale: 1.02 }}
        >
          {/* Logo - 2x larger */}
          <motion.img
            src={CASEY_LOGOS.light}
            alt="CASEY"
            className="h-14 md:h-16 w-auto object-contain opacity-90"
            transition={{ duration: 0.3 }}
          />
        </motion.a>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item, index) => (
            <motion.a
              key={item.label}
              href={item.href}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="relative text-sm font-medium font-inter text-white/70 hover:text-white transition-colors group"
            >
              <ScrambleText text={item.label} />
              <motion.span
                className="absolute -bottom-1 left-0 h-0.5 bg-white/50"
                initial={{ width: 0 }}
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
          ))}
          
          <DarkModeToggle />
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white/20 text-white/90 px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2 group hover:bg-white/30 transition-colors"
            onClick={() => window.open(companyInfo.website, "_blank")}
          >
            Website
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </motion.button>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <DarkModeToggle />
          <button
            className="p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <XIcon className="text-white/80" />
            ) : (
              <Menu className="text-white/80" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t bg-black/30 backdrop-blur-md border-white/10"
          >
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="block py-2 font-medium text-white/70 hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section with 3D transforms
const HeroSection = () => {
  const { scrollY } = useScroll();
  // Extended scroll thresholds - content stays visible much longer
  const y = useTransform(scrollY, [0, 800], [0, 150]);
  const opacity = useTransform(scrollY, [0, 600, 900], [1, 1, 0]); // Stays at full opacity until 600px scroll
  const scale = useTransform(scrollY, [0, 900], [1, 0.95]);
  const rotateX = useTransform(scrollY, [0, 900], [0, 5]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left - rect.width / 2) / 50);
    mouseY.set((e.clientY - rect.top - rect.height / 2) / 50);
  }, [mouseX, mouseY]);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-casey-blue pt-24"
      onMouseMove={handleMouseMove}
    >
      {/* Morphing Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-casey-blue via-[#0a2dd4] to-casey-purple" />
        
        {/* Animated Mesh Gradient */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(circle at 20% 50%, rgba(123,140,255,0.3) 0%, transparent 50%)",
              "radial-gradient(circle at 80% 50%, rgba(118,92,203,0.3) 0%, transparent 50%)",
              "radial-gradient(circle at 50% 80%, rgba(123,140,255,0.3) 0%, transparent 50%)",
              "radial-gradient(circle at 20% 50%, rgba(123,140,255,0.3) 0%, transparent 50%)"
            ]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        />

        {/* 3D Floating Shapes */}
        <motion.div
          style={{ x: mouseX, y: mouseY }}
          className="absolute top-1/4 left-1/4 w-64 h-64"
        >
          <motion.div
            animate={{ rotateY: 360, rotateX: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="w-full h-full border border-white/10 rounded-3xl"
            style={{ transformStyle: "preserve-3d" }}
          />
        </motion.div>

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 border border-white/5 rounded-full"
        />

        {/* Grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)`,
          backgroundSize: "80px 80px"
        }} />
      </div>

      <motion.div
        style={{ opacity, scale, rotateX, perspective: 1000 }}
        className="relative z-10 max-w-6xl mx-auto px-6 text-center"
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 text-white/90 text-sm font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            Pre-Seed Investment Opportunity
          </span>
        </motion.div>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-10"
        >
          <img
            src={CASEY_LOGOS.light}
            alt="CASEY Logo"
            className="h-32 md:h-44 lg:h-56 w-auto mx-auto object-contain"
          />
        </motion.div>

        {/* Main Title */}
        <h1 className="font-montserrat text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight font-bold">
          <RevealText delay={0.2}>
            <span className="block">AI Intelligence</span>
          </RevealText>
          <RevealText delay={0.4}>
            <span className="block bg-gradient-to-r from-white via-casey-light-blue to-white bg-clip-text text-transparent">
              for Careers
            </span>
          </RevealText>
        </h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto mb-4 font-inter font-light leading-relaxed"
        >
          The intelligence layer between careers and hiring
        </motion.p>
        
        {/* Supporting Line */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="text-sm md:text-base text-white/40 max-w-4xl mx-auto mb-14 font-inter"
        >
          Live today for individuals. Expanding to hiring and recruitment.
        </motion.p>

        {/* CTAs - Updated with App Demo button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center sm:items-start mb-20 px-4"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(255,255,255,0.2)" }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto bg-white text-casey-blue px-8 sm:px-10 py-4 rounded-full font-semibold text-base sm:text-lg group flex items-center justify-center gap-3 shadow-xl font-montserrat"
            onClick={() => document.getElementById("investment").scrollIntoView({ behavior: "smooth" })}
          >
            Investment Details
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </motion.button>
          <div className="flex flex-col items-center w-full sm:w-auto">
            <motion.a
              href="http://app.caseyai.co/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto bg-white text-casey-blue px-8 sm:px-10 py-4 rounded-full font-semibold text-base sm:text-lg transition-all flex items-center justify-center gap-3 group shadow-lg hover:shadow-xl"
            >
              <span>View Product Demo</span>
              <motion.span
                animate={{ x: [0, 4, 0], y: [0, -4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowUpRight className="w-5 h-5" />
              </motion.span>
            </motion.a>
            <span className="text-xs text-white/40 mt-2">Shows B2C product</span>
          </div>
        </motion.div>

        {/* Key Metrics - Updated with US Market label */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { value: "427", prefix: "$", suffix: "B+", label: "US Market Size" },
            { value: "1", prefix: "$", suffix: "M", label: "Pre-Seed Raise" },
            { value: "100", prefix: "$", suffix: "M", label: "ARR by 2028" },
            { value: "36", suffix: "M+", label: "US Students" }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.4 + index * 0.1, type: "spring", stiffness: 200 }}
              whileHover={{ y: -8, scale: 1.02, backgroundColor: "rgba(255,255,255,0.12)", transition: { duration: 0.15 } }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 cursor-default group"
            >
              <div className="text-3xl md:text-4xl font-montserrat font-bold text-white mb-2 group-hover:text-casey-light-blue transition-colors duration-150">
                <MorphNumber value={item.value} prefix={item.prefix} suffix={item.suffix} />
              </div>
              <div className="text-sm text-white/50">{item.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll Indicator - positioned below metrics */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="mt-16 md:mt-20"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-xs text-white/40 uppercase tracking-[0.3em]">Scroll</span>
            <ChevronDown className="w-5 h-5 text-white/40" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const { isDark } = useDarkMode();
  const iconMap = {
    users: Users,
    alert: AlertTriangle,
    building: Building2,
    "trending-down": TrendingDown
  };

  return (
    <section id="problem" className={`py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400"
          >
            The Problem
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`font-montserrat text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
              Careers and hiring{" "}
              <span className="text-rose-500">still run on resumes</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className={`text-base sm:text-lg mt-4 sm:mt-6 ${isDark ? "text-slate-400" : "text-slate-600"}`}
          >
            Resumes are static and shallow. They do not show how people think, what motivates them, or where they succeed.
          </motion.p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {problemStats.map((stat, index) => {
            const Icon = iconMap[stat.icon];
            return (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className={`h-full rounded-3xl p-8 border transition-all ${
                  isDark 
                    ? "bg-slate-800 border-slate-700 hover:border-rose-500/50 hover:shadow-xl hover:shadow-rose-500/10" 
                    : "bg-white border-slate-200 shadow-md hover:border-rose-200 hover:shadow-xl hover:shadow-rose-500/5"
                }`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${
                    isDark ? "bg-casey-light-blue/20 border border-casey-light-blue/30" : "bg-slate-900"
                  }`}>
                    <Icon className={`w-7 h-7 ${isDark ? "text-casey-light-blue" : "text-white"}`} />
                  </div>
                  <div className={`text-4xl font-montserrat font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                    {stat.value}
                  </div>
                  <div className={`text-lg font-semibold mb-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {stat.label}
                  </div>
                  <div className={`text-sm ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                    {stat.sublabel}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Problem Details */}
        <div className="grid md:grid-cols-3 gap-6">
          {problemDetails.map((detail, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className={`rounded-3xl p-8 group transition-colors border ${
                isDark 
                  ? "bg-slate-800 border-slate-700 hover:border-slate-600" 
                  : "bg-white border-slate-200 shadow-lg hover:shadow-xl"
              }`}
            >
              <h3 className={`text-xl font-montserrat font-semibold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                {detail.title}
              </h3>
              <p className={`text-base font-medium mb-6 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {detail.description}
              </p>
              <ul className="space-y-4">
                {detail.points.map((point, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className={`flex gap-3 text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                      isDark ? "bg-casey-light-blue" : "bg-casey-blue"
                    }`} />
                    <span>{point}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  const { isDark } = useDarkMode();
  const iconMap = {
    brain: Brain,
    messages: MessageSquare,
    map: Map,
    target: Target
  };

  const containerRef = useRef(null);

  return (
    <section id="solution" ref={containerRef} className={`py-32 overflow-hidden transition-colors duration-500 ${isDark ? "bg-slate-950" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
              isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
            }`}
          >
            The Solution
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`font-montserrat text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
              CASEY{" "}
              <span 
                style={{
                  background: "linear-gradient(to right, #011bc4, #765ccb)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}
              >
                Career Intelligence 2.0
              </span>
            </h2>
          </motion.div>
        </div>

        {/* Solution Modules - Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {solutionModules.map((module, index) => {
            const Icon = iconMap[module.icon];
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`group relative rounded-3xl p-8 border transition-all overflow-hidden ${
                  isDark 
                    ? "bg-slate-900 border-slate-800 hover:border-casey-blue/30 hover:shadow-2xl hover:shadow-casey-blue/10" 
                    : "bg-white border-slate-200 shadow-lg shadow-slate-200/50 hover:border-casey-blue/30 hover:shadow-xl hover:shadow-casey-blue/10"
                }`}
              >
                {/* Hover Gradient */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                  isDark ? "bg-gradient-to-br from-casey-blue/10 to-casey-purple/10" : "bg-gradient-to-br from-casey-blue/5 to-casey-purple/5"
                }`} />
                
                <div className="relative z-10">
                  <div className="flex items-start gap-6">
                    <motion.div
                      whileHover={{ rotate: 10, scale: 1.1 }}
                      className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                        isDark ? "bg-slate-700 border border-slate-600" : "bg-casey-blue"
                      }`}
                    >
                      <Icon className={`w-8 h-8 ${isDark ? "text-casey-light-blue" : "text-white"}`} />
                    </motion.div>
                    <div>
                      <h3 className={`text-xl font-montserrat font-semibold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                        {module.title}
                      </h3>
                      <p className={`mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                        {module.description}
                      </p>
                      <ul className="space-y-2">
                        {module.features.map((feature, i) => (
                          <li key={i} className={`flex items-center gap-2 text-sm ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                            <CheckCircle2 className={`w-4 h-4 ${isDark ? "text-casey-light-blue" : "text-casey-blue"}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Solution Note */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`text-center text-sm mt-10 mb-20 ${isDark ? "text-slate-500" : "text-slate-500"}`}
        >
          The same intelligence powers hiring when companies join CASEY.
        </motion.p>

        {/* CASEY Acronym */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-casey-blue rounded-3xl p-12 overflow-hidden"
        >
          <div className="absolute inset-0">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              className="absolute -top-1/2 -right-1/2 w-full h-full border border-white/5 rounded-full"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-1/2 -left-1/2 w-full h-full border border-white/5 rounded-full"
            />
          </div>

          <div className="relative z-10">
            <div className="flex justify-center mb-8">
              {/* Horizontal logo for dark blue background */}
              <img
                src={CASEY_LOGOS.light}
                alt="CASEY Logo"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>
            <h3 className="text-xl md:text-2xl font-montserrat font-semibold text-white text-center mb-10 md:mb-12">
              What <span className="font-bold text-casey-light-blue">CASEY</span> Stands For
            </h3>
            <div className="flex flex-col items-start max-w-md mx-auto space-y-4 md:space-y-6">
              {companyInfo.acronym.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-baseline cursor-default"
                >
                  <span className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-casey-light-blue">
                    {item.letter}
                  </span>
                  <span className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-white">
                    {item.word.slice(1).toLowerCase()}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Platform Showcase Section with Arrow Navigation
const PlatformShowcase = () => {
  const { isDark } = useDarkMode();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  
  const platformImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/vt0bkuc8_Screenshot%202025-12-01%20205505.png",
      title: "Adaptive Discovery Sessions"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/qb7ynczw_Screenshot%202025-12-01%20203857.png",
      title: "AI-matched Career Path"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/s84duvy6_Screenshot%202025-12-01%20205032.png",
      title: "Personalized Roadmap & Advice"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/pb7ygwmq_Screenshot%202025-12-01%20204803.png",
      title: "Gamified Progress"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/1zuadlbh_Screenshot%202025-12-03%20151438.png",
      title: "Analytics Tab"
    }
  ];

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.querySelector('.phone-card')?.offsetWidth || 280;
      const gap = 32; // gap-8 = 32px
      container.scrollTo({
        left: index * (cardWidth + gap),
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const scrollLeft = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    const newIndex = Math.min(platformImages.length - 1, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  return (
    <section className={`py-24 md:py-32 overflow-hidden transition-colors duration-500 relative ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium font-inter mb-6 ${
              isDark ? "bg-casey-blue/20 text-casey-light-blue" : "bg-casey-blue/10 text-casey-blue"
            }`}
          >
            The Platform
          </motion.span>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className={`font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
              Experience CASEY
            </h2>
            <h2 
              className="font-montserrat font-bold text-4xl md:text-5xl lg:text-6xl"
              style={{
                background: "linear-gradient(to right, #011bc4, #765ccb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
            >
              for Individuals
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className={`mt-6 text-base md:text-lg font-inter ${isDark ? "text-slate-400" : "text-slate-600"}`}
          >
            The individual product is how we build the intelligence.
          </motion.p>
          
          {/* App Demo CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <motion.a
              href="http://app.caseyai.co/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg font-montserrat shadow-lg ${
                isDark 
                  ? "bg-white text-slate-900 hover:bg-slate-100" 
                  : "bg-casey-blue text-white hover:opacity-90"
              }`}
            >
              <span>View Product Demo</span>
              <ArrowUpRight className="w-5 h-5" />
            </motion.a>
            <div className={`mt-4 text-xs space-y-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              <p>Now: Career clarity and living profiles</p>
              <p>Next: Hiring intelligence for companies</p>
            </div>
          </motion.div>
        </div>

        {/* App Screenshots with Arrow Navigation */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={scrollLeft}
            disabled={currentIndex === 0}
            className={`absolute left-0 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
              currentIndex === 0 
                ? "opacity-30 cursor-not-allowed" 
                : "opacity-100 hover:scale-110"
            } ${isDark ? "bg-slate-800 text-white" : "bg-white text-slate-900 shadow-lg"}`}
            data-testid="scroll-left-btn"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          
          <button
            onClick={scrollRight}
            disabled={currentIndex === platformImages.length - 1}
            className={`absolute right-0 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
              currentIndex === platformImages.length - 1 
                ? "opacity-30 cursor-not-allowed" 
                : "opacity-100 hover:scale-110"
            } ${isDark ? "bg-slate-800 text-white" : "bg-white text-slate-900 shadow-lg"}`}
            data-testid="scroll-right-btn"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Gradient Overlays */}
          <div className={`absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none ${
            isDark 
              ? "bg-gradient-to-r from-slate-900 to-transparent" 
              : "bg-gradient-to-r from-slate-50 to-transparent"
          }`} />
          <div className={`absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none ${
            isDark 
              ? "bg-gradient-to-l from-slate-900 to-transparent" 
              : "bg-gradient-to-l from-slate-50 to-transparent"
          }`} />

          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-6 md:gap-8 overflow-x-auto hide-scrollbar px-12 md:px-20 py-4 scroll-smooth"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {platformImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="phone-card flex-shrink-0"
                style={{ scrollSnapAlign: 'center' }}
              >
                {/* iPhone Frame */}
                <div className="relative">
                  {/* iPhone Body */}
                  <div className={`relative rounded-[2.5rem] md:rounded-[3rem] p-2 md:p-3 ${
                    isDark ? "bg-slate-800" : "bg-slate-900"
                  } shadow-2xl`}>
                    {/* Side buttons */}
                    <div className={`hidden md:block absolute -left-1 top-20 w-1 h-6 rounded-l ${isDark ? "bg-slate-700" : "bg-slate-800"}`} />
                    <div className={`hidden md:block absolute -left-1 top-32 w-1 h-10 rounded-l ${isDark ? "bg-slate-700" : "bg-slate-800"}`} />
                    <div className={`hidden md:block absolute -left-1 top-48 w-1 h-10 rounded-l ${isDark ? "bg-slate-700" : "bg-slate-800"}`} />
                    <div className={`hidden md:block absolute -right-1 top-28 w-1 h-14 rounded-r ${isDark ? "bg-slate-700" : "bg-slate-800"}`} />
                    
                    {/* Screen Container */}
                    <div className="relative rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-black">
                      {/* Dynamic Island */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 md:w-28 h-6 md:h-7 bg-black rounded-full z-20 flex items-center justify-center">
                        <div className="w-2 md:w-3 h-2 md:h-3 rounded-full bg-slate-800" />
                      </div>
                      
                      {/* Screenshot Image */}
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-56 md:w-64 h-[480px] md:h-[540px] object-cover object-top"
                      />
                      
                      {/* Home Indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-28 md:w-32 h-1 bg-white/50 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div className="mt-5 md:mt-6 text-center">
                  <h3 className={`font-montserrat font-semibold text-sm md:text-base ${isDark ? "text-white" : "text-slate-900"}`}>
                    {image.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Feature Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {[
            { icon: "💬", title: "Conversational AI", description: "Natural dialogue that adapts to your unique story" },
            { icon: "🎯", title: "Personalized Paths", description: "Roadmaps tailored to your goals and strengths" },
            { icon: "📱", title: "Mobile-First", description: "Career guidance in your pocket, anytime" }
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`text-center p-6 md:p-8 rounded-2xl md:rounded-3xl ${
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-slate-200 shadow-lg shadow-slate-200/50"
              } border`}
            >
              <div className="text-3xl md:text-4xl mb-4">{feature.icon}</div>
              <h3 className={`font-montserrat font-semibold text-base md:text-lg mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                {feature.title}
              </h3>
              <p className={`text-sm font-inter ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default function InvestorDeckV3() {
  return (
    <DarkModeProvider>
      <InvestorDeckContent />
    </DarkModeProvider>
  );
}

function InvestorDeckContent() {
  const { isDark } = useDarkMode();
  
  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? "bg-slate-950" : "bg-white"} cursor-none md:cursor-none`}>
      <CustomCursor />
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <PlatformShowcase />
      <MarketSectionV3 />
      <CompetitiveSectionV3 />
      <BusinessModelSectionV3 />
      <RoadmapSectionV3 />
      <TeamSectionV3 />
      <InvestmentSectionV3 />
      <FooterSectionV3 />
    </div>
  );
}

import {
  MarketSectionV3,
  CompetitiveSectionV3,
  BusinessModelSectionV3,
  RoadmapSectionV3,
  TeamSectionV3,
  InvestmentSectionV3,
  FooterSectionV3
} from "./InvestorDeckV3Part2";
